import { SentenceTaskProps } from "../SentenceTask.type";
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import styles from "./Conversation.module.scss";
import TaskPanel from "Components/TaskPanel";
import { StatusType, TaskType } from "App.types";
import { API_URL, STATIC_URL } from "App.constants";
import { encodeS3URI } from "App.helpers";
import { isMobile } from "react-device-detect";
import io, { Socket } from "socket.io-client";
import Cookies from "js-cookie";
import { horizontal } from "Components/useVisualizer";
import useVisualizerNew from "Components/useVisualizer/useVisualizerNew";
import { PCMPlayer } from "./PCMPlayer";
import cx from "classnames";
import { delay } from "lodash";
import { NotifyContext, UserContext } from "App";
import { AudioRecorder } from "./audio-recorder";
import RecordButton from "Components/RecordButton";

const ConversationSocket: FC<SentenceTaskProps> = ({ sentence, lesson, onTaskComplete, onNext }) => {
  const [connected, setConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<StatusType>(StatusType.Empty);
  const [muted, setMuted] = useState(false);
  const [audioRecorder] = useState(() => new AudioRecorder());

  const socket = useRef<Socket>();

  const { linkedStoryId: storyId = 0, media, voice } = sentence;

  // const { data: topic } = useSuspenseQuery({
  //   queryKey: ["topic", storyId],
  //   queryFn: () => API.conversation.getTopic(storyId),
  // });

  const audioContext = useRef<AudioContext>();
  const analyserRef = useRef<AnalyserNode>();
  const playerRef = useRef<PCMPlayer>();

  const ReactAudioViz = useVisualizerNew(analyserRef.current);

  useEffect(() => {
    return () => {
      socket.current?.close();
    };
  }, []);

  useEffect(() => {
    if (connected) {
      socket.current?.emit("push");
    }
  }, [connected]);

  const user = useContext(UserContext);
  const notifyApi = useContext(NotifyContext);

  const start = useCallback(() => {
    setIsLoading(true);

    const token = user?.token || Cookies.get("token");

    audioContext.current = new AudioContext({ sampleRate: 24_000 });
    analyserRef.current = audioContext.current.createAnalyser();
    playerRef.current = new PCMPlayer(audioContext.current, analyserRef.current);

    if (!socket.current?.connected) {
      socket.current = io(`${new URL(API_URL).protocol}//${new URL(API_URL).hostname}/conversation`, {
        query: { token, storyId, voice: voice || "Kore", lessonId: lesson.id },
      });

      socket.current.on("connected", async () => {
        console.log("client: connected to websocket");

        setConnected(true);
        setIsLoading(false);
      });

      socket.current.on("disconnect", () => {
        console.log("client: disconnected");
        setConnected(false);
        setIsLoading(false);
      });

      socket.current.on("audio", (data: any) => {
        setIsLoading(false);
        if (typeof data === "string") {
          const buffer = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
          const audioData = new Int16Array(buffer.buffer);

          playerRef.current?.playAudio(audioData);
        } else {
          const audioData = new Int16Array(data, 0, Math.floor(data.byteLength / 2));
          playerRef.current?.playAudio(audioData);
        }
      });

      socket.current.on("message", async (data: {}) => {
        console.log(data);
      });

      socket.current.on("hint", async (data) => {
        notifyApi.info({ message: <div dangerouslySetInnerHTML={{ __html: data }} /> });
      });

      socket.current.on("result", async (result: string) => {
        if (result === "completed") {
          setStatus(StatusType.Completed);

          onTaskComplete().then(() => {
            delay(() => socket.current?.disconnect(), 10000);
          });
        }
      });
    }
  }, [lesson.id, notifyApi, onTaskComplete, storyId, user?.token, voice]);

  useEffect(() => {
    const onData = (base64: string) => {
      socket.current?.emit("speech", base64);
    };
    if (connected && audioRecorder && !muted) {
      audioRecorder.on("data", onData).start();
    } else {
      audioRecorder.stop();
    }
    return () => {
      audioRecorder.off("data", onData);
    };
  }, [connected, audioRecorder, muted]);

  return (
    <div className={styles.conversation}>
      {media && !isMobile && (
        <div className={styles.imgContainer}>
          <img className={styles.picture} alt={""} src={`${STATIC_URL}/media/${encodeS3URI(media)}`} />
        </div>
      )}

      <TaskPanel
        isCompleted={status === StatusType.Completed}
        task={TaskType.Conversation}
        sentId={sentence.id}
        lessonId={lesson.id}
        storyId={storyId}
        onNext={onNext}
        rightButton={<div style={{ minWidth: 40 }} />}
      >
        <div className={styles.container}>
          <div className={styles.panelButtons}>
            {connected ? (
              <RecordButton onClick={() => setMuted((prev) => !prev)} loading={isLoading} isRecording={!muted && connected} />
            ) : (
              <Button loading={isLoading} onClick={start} type={"primary"}>
                start
              </Button>
            )}
          </div>

          <div className={cx(styles.audioViz)}>
            <ReactAudioViz
              model={horizontal({
                darkMode: false,
                reversed: false,
                scale: 1,
                frequencyRange: [0, 24000],
              })}
            />
          </div>
        </div>
      </TaskPanel>
    </div>
  );
};

export default ConversationSocket;
